import React, { useState } from 'react';
import { X, Lock, Globe, Plus, Trash2, Download, ShieldCheck, Crown } from 'lucide-react';
import { Job, JobCategory, CustomFieldDefinition, User } from '../types';
import { api } from '../api/client';

interface ProjectSettingsModalProps {
  job: Job;
  user: User | null;
  onClose: () => void;
  onSave: (updatedJob: Job) => void;
  onOpenAuth: () => void;
}

export const ProjectSettingsModal: React.FC<ProjectSettingsModalProps> = ({
  job,
  user,
  onClose,
  onSave,
  onOpenAuth,
}) => {
  const [title, setTitle] = useState(job.title);
  const [description, setDescription] = useState(job.description || '');
  const [category, setCategory] = useState<JobCategory>(job.category);
  const [isPrivate, setIsPrivate] = useState<boolean>(job.is_private);
  
  // Settings
  const [plantVariety, setPlantVariety] = useState(job.settings.plantVariety || '');
  const [sproutDate, setSproutDate] = useState(job.settings.sproutDate || '');
  const [lightSchedule, setLightSchedule] = useState(job.settings.lightSchedule || '18/6');
  const [currentStage, setCurrentStage] = useState(job.settings.currentStage || 'Vegetative');
  const [targetYield, setTargetYield] = useState(job.settings.targetYield || 500);
  const [yieldUnit, setYieldUnit] = useState(job.settings.yieldUnit || 'grams');

  // Custom Fields
  const [customFields, setCustomFields] = useState<CustomFieldDefinition[]>(
    job.settings.customFields || [
      { id: 'f_temp', name: 'Temperature', type: 'number', unit: '°F' },
      { id: 'f_humidity', name: 'Humidity', type: 'number', unit: '%' },
      { id: 'f_light', name: 'Light Cycle', type: 'text', unit: 'hrs' },
    ]
  );
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldUnit, setNewFieldUnit] = useState('');
  const [newFieldType, setNewFieldType] = useState<'number' | 'text'>('number');

  const [isSaving, setIsSaving] = useState(false);

  const handleAddCustomField = () => {
    if (!newFieldName.trim()) return;
    const fieldId = 'f_' + newFieldName.toLowerCase().replace(/[^a-z0-9]/g, '_');
    if (customFields.some((f) => f.id === fieldId)) {
      alert('A field with this name already exists.');
      return;
    }
    setCustomFields([
      ...customFields,
      {
        id: fieldId,
        name: newFieldName.trim(),
        type: newFieldType,
        unit: newFieldUnit.trim() || undefined,
      },
    ]);
    setNewFieldName('');
    setNewFieldUnit('');
  };

  const handleRemoveCustomField = (id: string) => {
    setCustomFields(customFields.filter((f) => f.id !== id));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isPrivate && (!user || !user.is_premium)) {
      alert('Private projects require an authenticated Pro account.');
      onOpenAuth();
      return;
    }

    setIsSaving(true);
    try {
      const updatedSettings = {
        ...job.settings,
        plantVariety,
        sproutDate,
        lightSchedule,
        currentStage,
        targetYield: Number(targetYield),
        yieldUnit,
        customFields,
      };

      const updatedJob: Job = {
        ...job,
        title: title.trim(),
        description: description.trim(),
        category,
        is_private: isPrivate,
        settings: updatedSettings,
        updated_at: Math.floor(Date.now() / 1000),
      };

      await api.saveProject(job.slug, updatedJob);
      onSave(updatedJob);
      onClose();
    } catch (err: any) {
      alert('Failed to save settings: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleExportJSON = () => {
    const blob = new Blob([JSON.stringify(job, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${job.slug}-backup.json`;
    a.click();
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
          <h2 style={{ fontSize: '1.35rem', color: 'var(--text-main)' }}>Project Settings & Custom Fields</h2>
          <button className="btn btn-secondary btn-icon" onClick={onClose} style={{ padding: '0.4rem' }}>
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* General info */}
          <div className="form-group">
            <label className="form-label">Project Title</label>
            <input
              type="text"
              className="form-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Description / Bio</label>
            <textarea
              className="form-textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Summary of this project or grow setup..."
              rows={2}
            />
          </div>

          {/* Privacy Security Setting */}
          <div className="form-group">
            <label className="form-label">Privacy & Access</label>
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.35rem' }}>
              <div
                style={{
                  flex: 1,
                  padding: '1rem',
                  borderRadius: 'var(--radius-md)',
                  border: !isPrivate ? '1px solid var(--accent-emerald)' : '1px solid var(--border-subtle)',
                  background: !isPrivate ? 'rgba(16, 185, 129, 0.1)' : 'rgba(255, 255, 255, 0.02)',
                  cursor: 'pointer',
                }}
                onClick={() => setIsPrivate(false)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: 4 }}>
                  <Globe size={16} color="var(--accent-emerald-light)" />
                  <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>Free Public (ntfy style)</span>
                </div>
                <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                  Anyone with the obfuscated link can view. No login needed.
                </p>
              </div>

              <div
                style={{
                  flex: 1,
                  padding: '1rem',
                  borderRadius: 'var(--radius-md)',
                  border: isPrivate ? '1px solid var(--accent-purple)' : '1px solid var(--border-subtle)',
                  background: isPrivate ? 'rgba(139, 92, 246, 0.12)' : 'rgba(255, 255, 255, 0.02)',
                  cursor: 'pointer',
                  position: 'relative',
                }}
                onClick={() => {
                  if (!user || !user.is_premium) {
                    onOpenAuth();
                  } else {
                    setIsPrivate(true);
                  }
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: 4 }}>
                  <Lock size={16} color="var(--accent-purple)" />
                  <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>Private Pro Lock</span>
                  <span className="badge badge-purple" style={{ fontSize: '0.65rem' }}>PRO</span>
                </div>
                <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                  Restricted to your authenticated Google Account.
                </p>
              </div>
            </div>
          </div>

          {/* Plant & Category Specific Fields */}
          <div className="form-group">
            <label className="form-label">Plant & Work Attributes</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem' }}>
              <div>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-sub)' }}>Variety / Strain</span>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g. Blue Dream, Beefsteak"
                  value={plantVariety}
                  onChange={(e) => setPlantVariety(e.target.value)}
                />
              </div>

              <div>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-sub)' }}>Sprout Date</span>
                <input
                  type="date"
                  className="form-input"
                  value={sproutDate}
                  onChange={(e) => setSproutDate(e.target.value)}
                />
              </div>

              <div>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-sub)' }}>Light Schedule</span>
                <select
                  className="form-select"
                  value={lightSchedule}
                  onChange={(e) => setLightSchedule(e.target.value)}
                >
                  <option value="18/6">18/6 (Veg)</option>
                  <option value="12/12">12/12 (Flower)</option>
                  <option value="20/4">20/4 (Auto)</option>
                  <option value="24/0">24/0 (Continuous)</option>
                  <option value="Natural">Natural Sunlight</option>
                </select>
              </div>

              <div>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-sub)' }}>Growth Stage</span>
                <select
                  className="form-select"
                  value={currentStage}
                  onChange={(e: any) => setCurrentStage(e.target.value)}
                >
                  <option value="Germination">Germination</option>
                  <option value="Seedling">Seedling</option>
                  <option value="Vegetative">Vegetative</option>
                  <option value="Flowering">Flowering</option>
                  <option value="Harvesting">Harvesting</option>
                  <option value="Curing">Curing</option>
                  <option value="Complete">Complete</option>
                </select>
              </div>

              <div>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-sub)' }}>Target Yield ({yieldUnit})</span>
                <input
                  type="number"
                  className="form-input"
                  value={targetYield}
                  onChange={(e) => setTargetYield(Number(e.target.value))}
                />
              </div>
            </div>
          </div>

          {/* Custom Fields Manager */}
          <div className="form-group">
            <label className="form-label">Custom Logging Fields</label>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-sub)', marginBottom: '0.5rem' }}>
              Add metrics to track consistently on each entry without writing in notes.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '0.75rem' }}>
              {customFields.map((field) => (
                <div
                  key={field.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: 'rgba(255, 255, 255, 0.03)',
                    padding: '0.45rem 0.85rem',
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--border-subtle)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{field.name}</span>
                    <span className="badge badge-emerald" style={{ fontSize: '0.7rem' }}>
                      {field.type} {field.unit ? `(${field.unit})` : ''}
                    </span>
                  </div>

                  <button
                    type="button"
                    className="btn btn-secondary btn-icon"
                    style={{ padding: '0.25rem', color: 'var(--accent-rose)' }}
                    onClick={() => handleRemoveCustomField(field.id)}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </div>

            {/* Add New Field Row */}
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input
                type="text"
                className="form-input"
                placeholder="Field Name (e.g. pH, EC, Light PPFD)"
                style={{ flex: 2, padding: '0.5rem 0.75rem', fontSize: '0.85rem' }}
                value={newFieldName}
                onChange={(e) => setNewFieldName(e.target.value)}
              />
              <input
                type="text"
                className="form-input"
                placeholder="Unit (e.g. ppm, L)"
                style={{ flex: 1, padding: '0.5rem 0.75rem', fontSize: '0.85rem' }}
                value={newFieldUnit}
                onChange={(e) => setNewFieldUnit(e.target.value)}
              />
              <select
                className="form-select"
                style={{ flex: 1, padding: '0.5rem', fontSize: '0.85rem' }}
                value={newFieldType}
                onChange={(e: any) => setNewFieldType(e.target.value)}
              >
                <option value="number">Number</option>
                <option value="text">Text</option>
              </select>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleAddCustomField}
              >
                <Plus size={14} /> Add
              </button>
            </div>
          </div>

          {/* Backup / Export */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid var(--border-subtle)' }}>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={handleExportJSON}
            >
              <Download size={13} />
              <span>Export JSON Backup</span>
            </button>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
